import { useTranslation } from 'next-i18next'
import React from 'react'

import { useAdminMeQuery } from '@front/api/service-admin'
import {
  adminToSegmentProperties,
  getDefaultAdminSegmentProperties,
  type SegmentAdditionalProperties,
  type SegmentAdminProperties,
  track,
} from '@front/logic/segment'
import { getAnalytics } from '@front/logic/segment/global.analytics'
import type { AdminMe } from '@front/type/admin'

interface TrackEventProps {
  event: string
  customAdmin?: AdminMe
  additionalProperties?: SegmentAdditionalProperties
}

interface TrackingProps {
  isPublicPage?: boolean
}

interface TrackingHook {
  track: (props: TrackEventProps) => void
  user?: AdminMe
}

export const useAdminTracking = ({
  isPublicPage = false,
}: TrackingProps = {}): TrackingHook => {
  const { data: admin } = useAdminMeQuery({ enabled: !isPublicPage })

  const {
    i18n: { language },
  } = useTranslation()

  const user = admin

  const trackEvent = React.useCallback(
    ({ event, customAdmin, additionalProperties }: TrackEventProps) => {
      const userToUse = customAdmin || user

      const userData: Partial<SegmentAdminProperties> = userToUse
        ? adminToSegmentProperties(userToUse, language)
        : getDefaultAdminSegmentProperties()

      track(
        event,
        {
          ...userData,
          ...additionalProperties,
        },
        getAnalytics(),
      )
    },
    [language, user],
  )

  return { track: trackEvent, user }
}
